import type { BotDifficulty } from '@cribbage/bots';
import { useAuthStore } from '~/store/authStore.js';
import { type AvatarId, FREE_AVATARS, isAvatarId, ownFace } from '~/store/avatars.js';

/**
 * Which face each seat at a table wears.
 *
 * Every person wears the face they chose (Mitch, 2026-10-02): You the one in
 * Settings, the others the one the game summary, the lobby or the invitation
 * carries for their seat. Two seats may wear the same face, and a pick is never
 * changed to keep a table unique: the team-colored plate behind each face tells
 * them apart (Mitch: "People would be 10/10 annoyed if they selected an avatar
 * and they couldn't use it or we changed it automatically.").
 *
 * A person whose choice has not reached this device (a pass-and-play seat, an
 * online seat before the summary answers, a deleted player) wears a free
 * stand-in: "You first, then seat order" (Mitch, 2026-10-01), the Fox, Owl, Dog
 * and Cat you are not wearing, by seat number. A bot takes none, so it never
 * uses up a face. A table with no You (pass-and-play) hands them out in seat
 * order: Fox, Owl, Dog, Cat.
 *
 * The answer reads a seat's number and its bot flag, never its live
 * connection, so a person who drops keeps their face and nobody after them
 * moves up. Every surface that draws a head (the table, the game over, the
 * Resume door) asks this, so one person wears one face across all of them.
 *
 * Plain TypeScript with no React. It sits in `session/`, below the table,
 * because a saved game's Resume door asks it too.
 */

/** Which drawing a seat wears. A bot is never given a face; see `BotFace`. */
export type FaceKind = 'you' | 'person' | 'bot' | 'guest';

/** Which catalog face a human seat wears. Which seat wears which is `seatLooks` below. */
export type FaceLook = AvatarId;

/**
 * What decides the drawing a head wears: its kind, a person's face, and a
 * bot's level. Every seat shape that draws a head (`CrossSeat`, `ClassicSeat`,
 * `ClassicMember`, `ClassicSide`, `EndSide`, `MatchSeat`, `ResumeSide`,
 * `HandoffReceiver`) carries these three, and `Avatar` takes them, so they are
 * declared once here. A shape that only ever holds
 * some kinds narrows `Kind`.
 */
export interface SeatFace<Kind extends FaceKind = FaceKind> {
  /** Which drawing. A bot is never given a face. */
  readonly kind: Kind;
  /** Which face a person wears, from `seatLooks`. Unset, the kind picks (`kindLook`). */
  readonly look?: FaceLook | undefined;
  /**
   * The level of the bot dealt this seat, which picks its house bot. Unset on a
   * person, on a bot covering a person who left, and where the level is not
   * known: those bots keep the plain mark.
   */
  readonly botLevel?: BotDifficulty | undefined;
}

/**
 * A head's face, read off any seat shape. It owns the one rule about levels:
 * only a bot wears one, so a level that reaches a person's seat is dropped.
 * Builders spread it into a seat, and every `Avatar` call site spreads it into
 * the head.
 */
export function seatFace<Kind extends FaceKind>(seat: SeatFace<Kind>): SeatFace<Kind> {
  return {
    kind: seat.kind,
    look: seat.look,
    botLevel: seat.kind === 'bot' ? seat.botLevel : undefined,
  };
}

/** One seat, as the face order reads it. */
export interface FaceSeat {
  readonly seat: number;
  /** The seat was dealt to a bot. Its flag, not a bot standing in for a person who left. */
  readonly isBot: boolean;
  /** The viewer sits here. At most one seat, and none in pass-and-play. */
  readonly isYou: boolean;
  /**
   * The face this seat's player chose, when the device knows it. A key this
   * build has no art for counts as unknown, and the seat wears a stand-in.
   */
  readonly avatarId?: string | null | undefined;
}

/** The signed-in player's face, outside React. A component uses `useOwnFace`. */
export function currentOwnFace(): FaceLook {
  return ownFace(useAuthStore.getState().user?.avatar_id);
}

/**
 * Each human seat's look, by seat number. A bot's seat has no entry.
 *
 * `you` is the viewer's own face. A component passes `useOwnFace()`, so its
 * heads follow `/me` when it answers after the first draw. The default reads
 * the store once, which suits a caller with no React to subscribe through.
 */
export function seatLooks(
  seats: readonly FaceSeat[],
  you: FaceLook = currentOwnFace(),
): ReadonlyMap<number, FaceLook> {
  const people = seats
    .filter((entry) => entry.isYou || !entry.isBot)
    .sort((a, b) => Number(b.isYou) - Number(a.isYou) || a.seat - b.seat);
  const hasYou = people[0]?.isYou === true;
  const others = hasYou ? FREE_AVATARS.filter((face) => face !== you) : FREE_AVATARS;
  const looks = new Map<number, FaceLook>();
  people.forEach((entry, index) => {
    const look = entry.isYou
      ? you
      : isAvatarId(entry.avatarId)
        ? entry.avatarId
        : others[hasYou ? index - 1 : index];
    if (look !== undefined) looks.set(entry.seat, look);
  });
  return looks;
}

/**
 * The look a head wears when its caller has no table to ask about: You your
 * own face, and a person the first free face you are not wearing, the one the
 * person opposite You gets.
 */
export function kindLook(kind: 'you' | 'person', you?: FaceLook): FaceLook;
export function kindLook(kind: FaceKind, you?: FaceLook): FaceLook | undefined;
export function kindLook(kind: FaceKind, you: FaceLook = currentOwnFace()): FaceLook | undefined {
  if (kind === 'you') return you;
  if (kind === 'person') return FREE_AVATARS.find((face) => face !== you);
  return undefined;
}
